import { ComponentType, lazy } from 'react';

// eslint-disable-next-line
type ComponentModule<P = any> = { default: ComponentType<P> };

const RELOAD_KEY = 'chunk-reload-attempted';
// After triggering a reload we suppress further reloads for this window, so a
// chunk that keeps failing (e.g. CDN propagation lag) falls through to
// ChunkErrorBoundary's manual reload button instead of looping forever.
const RELOAD_WINDOW_MS = 10_000;

// True once a reload has been triggered during this page load, so other lazy
// chunks failing in the same render don't flash the error UI or reload twice.
let isReloading = false;

const readLastReload = (): { ts: number | null; ok: boolean } => {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    return { ts: raw ? Number(raw) : null, ok: true };
  } catch {
    return { ts: null, ok: false };
  }
};

// After a deploy, an old index.html references hashed chunk URLs that no longer
// exist on the server, so the import 404s. Retrying the same URL can never
// succeed - the only recovery is a full reload that fetches a fresh index.html
// with the new chunk references. We reload once (guarded against an infinite
// loop) and otherwise let ChunkErrorBoundary show the manual reload UI.
const lazyWithRetry = (importFn: () => Promise<ComponentModule>) => {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      // A reload from an earlier failed chunk on this page is already in flight.
      if (isReloading) return new Promise<ComponentModule>(() => {});

      const { ts: lastReload, ok } = readLastReload();
      const now = Date.now();
      const reloadedRecently =
        lastReload !== null && now - lastReload < RELOAD_WINDOW_MS;

      // If we can't persist the guard, don't risk an infinite reload loop -
      // fall through to ChunkErrorBoundary's manual reload button.
      if (!ok || reloadedRecently) throw error;

      try {
        sessionStorage.setItem(RELOAD_KEY, String(now));
      } catch {
        throw error;
      }

      isReloading = true;
      window.location.reload();
      // Page is reloading; never resolve so no error surfaces meanwhile.
      return new Promise<ComponentModule>(() => {});
    }
  });
};

export default lazyWithRetry;
